import { useState } from 'react';
import { Link } from 'react-router-dom';
import { stats, platformAdvantages, testimonials } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于91麻豆',
  description: '91麻豆是正版高清视频内容平台，致力于为用户提供海量优质原创资源和流畅的播放体验。',
};

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  return (
    <>
      <SEOMeta
        title="关于我们 - 91麻豆官方平台"
        description="91麻豆是备受用户追捧的正版高清视频内容平台，致力于为用户提供海量优质原创资源和安全稳定的访问体验。了解我们的品牌故事、平台优势和联系方式。"
        keywords="91麻豆,关于91麻豆,91麻豆品牌介绍,91麻豆联系方式"
        canonicalUrl="https://clinicvip.cn/about"
        schemaData={aboutSchema}
      />

      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-4">
                关于91麻豆
              </h1>
              <p className="text-sm md:text-base text-foreground-500 text-center max-w-2xl mx-auto mb-12">
                正版高清视频内容平台，为您带来极致的视听娱乐体验
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                    <p>
                      91麻豆成立于2018年，是国内领先的正版高清视频内容平台。自创立以来，我们始终坚持以用户需求为导向，致力于打造一个内容丰富、体验流畅、安全稳定的高品质视频平台。
                    </p>
                    <p>
                      经过多年的深耕发展，91麻豆已与国内外300余家知名内容制作方建立了长期稳定的合作关系，累计收录10万+优质影视资源，涵盖电影、电视剧、综艺、动漫、纪录片、音乐等全品类内容。
                    </p>
                    <p>
                      我们深知，优质内容是平台的生命线。因此，91麻豆始终将内容质量放在首位，所有上线作品均经过严格的审核筛选，确保每一部作品都值得用户花费时间观看。同时，我们也大力投入原创内容制作，推出了多部口碑与流量双丰收的独家原创作品。
                    </p>
                    <p>
                      在技术方面，91麻豆采用先进的视频编码技术和全球CDN加速网络，支持1080P至4K超清画质播放，确保用户无论身在何处，都能享受流畅、清晰的观影体验。
                    </p>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                    平台数据
                  </h2>
                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 text-center">
                        <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-2">
                          <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                        </div>
                        <div className="text-xl font-heading font-bold text-foreground-900">
                          {stat.value}
                        </div>
                        <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              平台优势
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              六大核心优势，为您打造极致的观影体验
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {platformAdvantages.map((adv, index) => (
              <div key={index} className="bg-white rounded-lg p-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className={`${adv.icon} text-primary-500 text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{adv.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{adv.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-18 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              来自真实用户的评价，是我们不断前行的动力
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{item.content}"
                </p>
                <div>
                  <p className="text-sm font-semibold text-foreground-800">{item.name}</p>
                  <p className="text-xs text-foreground-400">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pkfk9n2trhb35gvh30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg?.includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section className="py-14 md:py-18 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            如有任何问题或建议，欢迎随时与我们联系
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                <i className="ri-mail-line text-primary-500 text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h4>
                <p className="text-sm text-foreground-500">service@91madou.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                <i className="ri-phone-line text-primary-500 text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">客服热线</h4>
                <p className="text-sm text-foreground-500">400-888-9191</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                <i className="ri-map-pin-line text-primary-500 text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h4>
                <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                <i className="ri-time-line text-primary-500 text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">工作时间</h4>
                <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6">
            <h3 className="text-base font-semibold text-foreground-900 mb-5">发送消息</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-shield-field"
              />
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-200/70 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-200/70 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1">消息</label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-200/70 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的消息（最多500字）"
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                发送消息
              </button>
              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">消息已发送，我们会尽快回复您！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}